<template>
  <closeable-dialog
    v-model="keyboardStore.settingsOpen"
    max-width="640px"
    :close-offset-x="24"
  >
    <div class="pycad-controls">
      <div class="pycad-controls__header">
        <div class="pycad-controls__icon">
          <i class="mdi mdi-keyboard-outline"></i>
        </div>
        <div>
          <div class="pycad-controls__title">
            Keyboard Shortcuts & View Controls
          </div>
          <div class="pycad-controls__subtitle">
            Work faster with hotkeys and mouse gestures
          </div>
        </div>
      </div>

      <section class="pycad-controls__section">
        <div class="pycad-controls__section-title">View Controls</div>
        <div class="pycad-controls__rows">
          <div
            v-for="control in mouseControls"
            :key="control.label"
            class="pycad-controls__row"
          >
            <span class="pycad-controls__label">
              <i :class="['mdi', control.icon]"></i>
              {{ control.label }}
            </span>
            <span class="pycad-controls__value">{{ control.value }}</span>
          </div>
        </div>
      </section>

      <section class="pycad-controls__section">
        <div class="pycad-controls__section-title">Keyboard Shortcuts</div>
        <div class="pycad-controls__rows">
          <div
            v-for="[action, key] in bindings"
            :key="action"
            class="pycad-controls__row"
          >
            <span class="pycad-controls__label">{{ action }}</span>
            <span class="pycad-controls__keys">
              <kbd v-for="part in splitKey(key)" :key="part" class="pycad-kbd">
                {{ part }}
              </kbd>
            </span>
          </div>
        </div>
      </section>
    </div>
  </closeable-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { actionToKey } from '@/src/composables/useKeyboardShortcuts';
import { ACTIONS } from '@/src/constants';
import { useKeyboardShortcutsStore } from '@/src/store/keyboard-shortcuts';
import CloseableDialog from './CloseableDialog.vue';
import { getEntries } from '../utils';

const keyboardStore = useKeyboardShortcutsStore();

const mouseControls = [
  {
    icon: 'mdi-mouse-scroll-wheel',
    label: 'Scroll slices',
    value: 'Mouse wheel or 2-finger vertical scroll',
  },
  {
    icon: 'mdi-magnify-plus-outline',
    label: 'Zoom',
    value: 'Right mouse button + move vertically',
  },
  {
    icon: 'mdi-hand-back-right-outline',
    label: 'Pan',
    value: 'Shift + left mouse button + move',
  },
];

const bindings = computed(() =>
  getEntries(actionToKey.value).map(
    ([action, key]) => [ACTIONS[action].readable, key] as [string, string]
  )
);

function splitKey(key: string): string[] {
  return key
    .split('+')
    .map((part) => {
      const p = part.trim();
      if (p.length === 1) return p.toUpperCase();
      // capitalize modifier / named keys (ctrl -> Ctrl, arrowdown -> Arrowdown)
      return p.charAt(0).toUpperCase() + p.slice(1);
    })
    .filter(Boolean);
}
</script>

<style scoped>
.pycad-controls {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 12px);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--pycad-text-primary, #fff);
  max-height: 80vh;
  overflow-y: auto;
}

.pycad-controls__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-right: 40px; /* keep clear of the dialog's floating close button */
}

.pycad-controls__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: var(--pycad-radius-sm, 8px);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.12));
  border: 1px solid var(--pycad-primary-border, rgba(255, 153, 0, 0.3));
  color: var(--pycad-primary, #ff9900);
  font-size: 20px;
}

.pycad-controls__title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.pycad-controls__subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-controls__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: var(--pycad-surface, #0d0d0d);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 10px);
}

.pycad-controls__section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pycad-primary, #ff9900);
}

.pycad-controls__rows {
  display: flex;
  flex-direction: column;
}

.pycad-controls__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 4px;
  border-bottom: 1px solid var(--pycad-border, #222);
}

.pycad-controls__row:last-child {
  border-bottom: none;
}

.pycad-controls__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--pycad-text-primary, #fff);
  min-width: 0;
}

.pycad-controls__label i {
  font-size: 15px;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-controls__value {
  font-size: 12px;
  color: var(--pycad-text-secondary, #a0a0a0);
  text-align: right;
}

.pycad-controls__keys {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
</style>
